/* @flow */
/*
 * Sometimes, we want to use touchstart, touchmove, and touchend to try to
 * detect something like a click without relying on an actual click event.
 * Most notably, on older iOS devices we use a UIWebView in order to make video
 * streaming work without some of the nice properties that WKWebView didn't add
 * until iOS 10. This requires us to use FastClick to avoid the 300ms delay.
 * This prevents the delay, but also adds some new edge cases to work around. In
 * particular, tapping rapidly will ignore roughly every other tap.
 *
 * This helper is intended to be used when click handlers needs to be processed
 * rapidly based on touch events instead of click events.
 *
 * This function takes a click handler and expands it into touchstart and
 * touchend events instead, which can be spread onto the component props.
 *
 * Usage:
 *   class SomeComponent extends React.Component {
 *     render() {
 *       const handleClick = () => {
 *         // some logic
 *       };
 *       return <SomeChild {...touchHandlersFromClickHandlers(handleClick)} />
 *     }
 *   }
 *
 * NOTE: Be careful not to add onTouchStart, etc handlers as props without
 * carefully combining them with the responses from this helper.
 */

type onClickResult = {|
  onClick: (MouseEvent) => void,
  onMouseDown: ?(MouseEvent) => void,
  onMouseUp: ?(MouseEvent) => void,
|};
type onTouchResult = {|
  onTouchStart: (TouchEvent) => void,
  onTouchMove: (TouchEvent) => void,
  onTouchEnd: (TouchEvent) => void,
|};

const DRAG_CUTOFF = 20;

const isTouchDevice = "ontouchstart" in window;

export default function touchHandlersFromClickHandlers(
  handler: (MouseEvent | TouchEvent) => void,
  downHandler: ?(MouseEvent | TouchEvent) => void,
  upHandler: ?(MouseEvent | TouchEvent) => void
): onClickResult | onTouchResult {
  if (!isTouchDevice) {
    // We won't get any touch events, so rely on normal clicks.
    return {
      onClick: handler,
      onMouseDown: downHandler,
      onMouseUp: upHandler,
    };
  }

  // fire the click handler if
  //   1) the touchstart and touchend are around the same place (no dragging)
  //   2) less than 500ms has passed (otherwise it's a "long press". Probably. Couldn't find standards)
  let time;
  let xcoord;
  let ycoord;

  return {
    onTouchStart(event) {
      time = Date.now();
      xcoord = event.changedTouches[0].screenX;
      ycoord = event.changedTouches[0].screenY;
      if (downHandler != null) {
        downHandler.call(this, event);
      }
    },

    onTouchMove(event) {
      const newX = event.changedTouches[0].screenX;
      const newY = event.changedTouches[0].screenY;
      if (
        xcoord < newX - DRAG_CUTOFF ||
        xcoord > newX + DRAG_CUTOFF ||
        ycoord < newY - DRAG_CUTOFF ||
        ycoord > newY + DRAG_CUTOFF
      ) {
        // stop listening if the user moves too much
        time = null;
      }
    },

    onTouchEnd(...args) {
      if (upHandler != null) {
        upHandler.call(this, ...args);
      }
      if (!time || time + 500 < Date.now()) {
        // touchend was called after too much time or a move
        return;
      }
      time = null;
      handler.call(this, ...args);
    },
  };
}



// WEBPACK FOOTER //
// ./src/components/utils/touchHandlersFromClickHandlers.js